import { darken, transparentize } from 'polished';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp, pixelToRem, unlessProp, whenProp } from 'remote-com-shared/style/helpers';
import { sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import { fromTabletBreakpoint } from '@/src/domains/shared/breakpoints';

export const ContentSectionBackground = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 0;
  width: 50%;
  height: auto;
`;

export const ContentSectionIcon = styled.div`
  margin-bottom: 16px;
`;

export const ContentSectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  > * + * {
    margin-left: 16px;
  }
`;

export const ContentSectionTitle = styled.div`
  font-family: var(--typography-fontBrand);
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${fluidProp('--font-size', '48px', '60px')}
  font-size: var(--font-size);
  line-height: 0.96;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 24px;
  word-break: break-word;
  svg {
    ${fluidProp('margin-right', '10px', '20px')}
  }
  > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    margin: 0;
  }
`;

export const ContentSectionSubTitle = styled.div`
  font-family: var(--typography-fontBrand);
  font-size: ${pixelToRem('40px')};
  line-height: 1.45;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 0;

  > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    margin: 0;
  }
`;

export const ContentSectionButton = styled.span`
  // Button reset
  border: none;
  cursor: pointer;

  display: inline-block;
  text-align: center;
  font-size: ${pixelToRem('16px')};
  line-height: 1.5;
  font-weight: 500;
  padding: 12px 28px;
  border-radius: 2em;
  color: #fff;
  background-color: #00b568;
  box-sizing: border-box;
  min-width: 200px;
  ${sharedTransition('background-color')}

  &:hover {
    background-color: ${darken(0.1, '#00b568')};
  }
`;

export const ContentSection = styled.section`
  --sectionColor: var(--colors-bayoux);
  --sectionHighlightColor: var(--colors-darkBlue);
  --sectionBackgroundColor: var(--colors-blank);

  color: var(--sectionColor);
  background-color: var(--sectionBackgroundColor);
  font-size: ${pixelToRem('18px')};
  line-height: 1.44;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  strong,
  a {
    color: var(--sectionHighlightColor);
  }

  ${fluidProp('--spacing-y', '60px', '128px')}
  position: relative;
  ${unlessProp(
    ['$overlapTop', '$flattenTop'],
    css`
      padding-top: var(--spacing-y);
    `
  )}
  ${unlessProp(
    ['$overlapBottom', '$flattenBottom'],
    css`
      padding-bottom: var(--spacing-y);
    `
  )}

  ${ContentContainer} {
    position: relative;
    z-index: 1;
  }

  &:nth-child(odd) {
    --sectionColor: ${({ theme }) => transparentize(0.5, theme.colors.blank)};
    --sectionHighlightColor: var(--colors-blank);
    --sectionBackgroundColor: var(--colors-darkBlue);
  }

  ${whenProp(
    '$overlapTop',
    css`
      && {
        background-color: transparent;

        ${ContentContainer} {
          ${fluidProp('margin-top', '-50px', '-100px')}
        }
      }
    `
  )}

  // Used to "merge" sections due to :nth-child(odd) background
  ${whenProp(
    '$filler',
    css`
      padding: 0;
    `
  )}

  // Used to ensure spacing on overlapping sections
  ${whenProp(
    '$fillerHalf',
    css`
      ${fluidProp('--spacing-y', '30px', '64px')}
    `
  )}
`;

export const ContentSectionSplit = styled.div`
  flex-grow: 1;
  display: grid;
  gap: 58px;

  ${fromTabletBreakpoint(css`
    grid-template-columns: repeat(auto-fit, minmax(326px, 1fr));
  `)}
`;
export const ContentSectionSplitColumn = styled.div`
  position: relative;
`;

export const ContentSectionSplitFillerColumn = styled.div`
  display: none;
  ${fromTabletBreakpoint(css`
    display: block;
  `)}
`;

export const ContentSectionSplitUnderlay = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 50vh;

  ${fromTabletBreakpoint(css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 0;
    min-height: 0vh;
  `)}
`;
